// Insights/index.jsx — the article index ("/insights").
// Carries .home-page so the palette, type scale and primitives come from
// homePage.css, the same way the job seeker and career centre pages do.
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import { audiences, articlesFor, ctaFor, isExternal } from "../../content/articles";
import { FadeIn } from "../ui/fade-in";
import Seo from "../../seo/Seo";
import "../HomePage/homePage.css";
import "./insights.css";

/* The career centre card: cover, category, title, read time, then the link. */
const ArticleCard = ({ article }) => {
  const { cover, tone, title, readTime, url } = article;

  const inner = (
    <>
      <span className={`res-cover res-${tone}`} aria-hidden="true">
        <img decoding="async" src={cover} alt="" loading="lazy" />
      </span>

      <span className="res-title">{title}</span>
      <span className="res-read">{readTime}</span>

      <span className="res-cta">
        {ctaFor(article)}
        <span className="res-cta-icon" aria-hidden="true">
          <ArrowRight size={12} strokeWidth={2.5} />
        </span>
      </span>
    </>
  );

  return isExternal(url) ? (
    <a className="res-card" href={url} target="_blank" rel="noopener noreferrer">
      {inner}
    </a>
  ) : (
    <Link className="res-card" to={url}>
      {inner}
    </Link>
  );
};

const InsightsPage = () => {
  const [audience, setAudience] = useState("all");
  const shown = articlesFor(audience);

  return (
    <div className="home-page res-page">
      <Seo path="/insights" />
      <section className="res-head">
        <div className="home-container">
          <FadeIn delay={0}>
            <span className="res-eyebrow">Insights</span>
          </FadeIn>

          <FadeIn delay={0.1}>
            <h1 className="res-h1">
              Reports and thinking from{" "}
              <br className="br-wide" />
              the team behind RAIN
            </h1>
          </FadeIn>

          <FadeIn delay={0.2}>
            <p className="res-lead">
              What we&rsquo;re seeing across partner campuses, written for the
              people running career services and the students using RAIN.
            </p>
          </FadeIn>
        </div>
      </section>

      <section className="res-body">
        <div className="home-container">
          <FadeIn delay={0.3}>
            <div
              className="res-filters"
              role="group"
              aria-label="Filter by audience"
            >
              {audiences.map(({ key, label }) => (
                <button
                  type="button"
                  key={key}
                  className={`res-chip${key === audience ? " res-chip-on" : ""}`}
                  onClick={() => setAudience(key)}
                  aria-pressed={key === audience}
                >
                  {label}
                </button>
              ))}
            </div>
          </FadeIn>

          {/* The grid arrives as one block rather than card by card: the cards
              stretch to a shared row height, and a wrapper per card would take
              that away and leave the footers ragged. */}
          <FadeIn delay={0.4}>
            <div className="res-grid">
              {shown.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default InsightsPage;
